import { useMutation } from '@tanstack/react-query';
import { registerUser } from 'api/userApi';
import { queryClient } from 'App';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import { useAuth } from 'context/AuthProvider';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { supabase } from 'supabase/client';
import { useDebounce } from 'use-debounce';
import * as Yup from 'yup';
import TurnstileCaptcha from './captcha/TurnstileCaptcha';
import SocialAuthButtons from './SocialAuthButtons';
import Divider from 'components/common/Divider';

const RegistrationForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/classe');
    }
  }, [user, navigate]);

  const [token, setToken] = useState('');

  const mutation = useMutation({
    mutationFn: registerUser,
    onSuccess: async ({ data: { access_token, refresh_token } }) => {
      const { error: sessionError } = await supabase.auth.setSession({
        access_token,
        refresh_token
      });
      if (sessionError) {
        throw sessionError;
      } else {
        queryClient.invalidateQueries({ queryKey: ['userData'] });
        window.location.href = '/classe';
      }
    },
    onError: err => {
      toast.error(err.response?.data?.message || err.message);
    }
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      isAccepted: false
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .matches(
          /^[a-zA-Z0-9_.]+$/,
          "L'username può contenere solo lettere, numeri, punti e trattini bassi."
        )
        .min(4, "L'username deve contenere almeno 4 caratteri.")
        .max(30, "L'username deve contenere al massimo 30 caratteri.")
        .required("L'username è obbligatorio."),
      email: Yup.string()
        .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, 'Email non valida.')
        .required("L'email è obbligatoria."),
      password: Yup.string()
        .min(6, 'La password deve essere lunga almeno 6 caratteri.')
        .required('La password è obbligatoria.'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Le password non coincidono.')
        .min(
          6,
          'La conferma della password deve essere lunga almeno 6 caratteri.'
        )
        .required('La conferma della password è obbligatoria.'),
      isAccepted: Yup.boolean()
        .oneOf([true], 'Devi accettare i termini e le condizioni.')
        .required('Devi accettare i termini e le condizioni.')
    }),
    onSubmit: async values => {
      const { username, email, password } = values;
      if (!token) {
        toast.error('Impossibile verificare il captcha');
        return;
      }
      setLoading(true);
      try {
        await mutation.mutateAsync({ username, email, password, token });
      } catch (error) {
        toast.error(`Impossibile creare l'account: ${error.message}`);
        // Reset Turnstile
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
      } finally {
        setLoading(false);
      }
    }
  });

  const [usernameNotAvaiableError, setUsernameNotAvaiableError] =
    useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const checkUsernameAvailability = async username => {
    setIsCheckingUsername(true);
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/check/username/${username}`
    );
    if (response.status === 200) {
      setUsernameNotAvaiableError(null);
    } else if (response.status === 422) {
      setUsernameNotAvaiableError("L'username non è disponibile.");
    } else {
      toast.error('Errore di validazione del nome utente');
    }
    setIsCheckingUsername(false);
  };

  const [debouncedUsername] = useDebounce(formik.values.username, 800);
  useEffect(() => {
    if (!formik.errors.username && debouncedUsername) {
      checkUsernameAvailability(debouncedUsername);
    } else {
      setIsCheckingUsername(false);
    }
  }, [debouncedUsername]);

  const turnstileRef = useRef(null);

  const handleTurnstileSuccess = useCallback(token => {
    setToken(token);
  }, []);

  return (
    <Form noValidate onSubmit={formik.handleSubmit} validateOnChange>
      <SocialAuthButtons authActionText="Iscriviti" />
      <Divider className="mt-4">oppure</Divider>
      <FloatingLabel
        controlId="floatingUsername"
        label="Username"
        className="mb-3"
      >
        <Form.Control
          name="username"
          type="text"
          value={formik.values.username}
          onChange={e => {
            setIsCheckingUsername(true);
            formik.handleChange(e);
          }}
          onBlur={formik.handleBlur}
          placeholder="Username"
          minLength={4}
          maxLength={30}
          pattern="[a-zA-Z0-9_.]+"
          isValid={
            !isCheckingUsername &&
            formik.touched.username &&
            !formik.errors.username &&
            !usernameNotAvaiableError
          }
          isInvalid={
            !isCheckingUsername &&
            formik.touched.username &&
            (formik.errors.username || usernameNotAvaiableError)
          }
          required
        />
        <Form.Control.Feedback>L'username è disponibile.</Form.Control.Feedback>
        <Form.Control.Feedback type="invalid">
          {formik.errors.username || usernameNotAvaiableError}
        </Form.Control.Feedback>
      </FloatingLabel>
      <FloatingLabel controlId="floatingEmail" label="Email" className="mb-3">
        <Form.Control
          name="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Email"
          isInvalid={formik.touched.email && formik.errors.email}
          isValid={formik.touched.email && !formik.errors.email}
          required
        />
        <Form.Control.Feedback>L'email è valida.</Form.Control.Feedback>
        <Form.Control.Feedback type="invalid">
          {formik.errors.email}
        </Form.Control.Feedback>
      </FloatingLabel>

      <Row className="gx-2">
        <Form.Group as={Col} sm={6} className="mb-3">
          <FloatingLabel controlId="floatingPassword" label="Password">
            <Form.Control
              name="password"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Almeno 6 caratteri"
              minLength={6}
              isValid={formik.touched.password && !formik.errors.password}
              isInvalid={formik.touched.password && formik.errors.password}
              required
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.password}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} sm={6} className="mb-3">
          <FloatingLabel
            controlId="floatingConfirmPassword"
            label="Conferma password"
          >
            <Form.Control
              name="confirmPassword"
              type="password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Conferma password"
              minLength={6}
              isValid={
                formik.touched.confirmPassword && !formik.errors.confirmPassword
              }
              isInvalid={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
              required
            />
            <Form.Control.Feedback type="invalid">
              {!formik.errors.password ? formik.errors.confirmPassword : ''}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Form.Group>
      </Row>

      <Form.Group>
        <Form.Check type="checkbox" id="acceptCheckbox" className="mb-0">
          <Form.Check.Input
            name="isAccepted"
            type="checkbox"
            checked={formik.values.isAccepted}
            onChange={formik.handleChange}
            isValid={formik.touched.isAccepted && !formik.errors.isAccepted}
            required
          />
          <Form.Check.Label className="form-label mb-0">
            Accetto le{' '}
            <Link to="https://fantaclasse.it/condizioni-del-servizio">
              Condizioni del Servizio
            </Link>{' '}
            e l'
            <Link to="https://fantaclasse.it/privacy-policy/">
              Informativa sulla Privacy
            </Link>
          </Form.Check.Label>
        </Form.Check>
      </Form.Group>
      <TurnstileCaptcha onSuccess={handleTurnstileSuccess} ref={turnstileRef} />
      <Form.Group>
        <LoadingButton
          text="Crea account"
          isLoading={loading}
          disabled={
            !formik.isValid ||
            isCheckingUsername ||
            usernameNotAvaiableError ||
            !token
          }
          className="w-100"
          type="submit"
        />
      </Form.Group>
    </Form>
  );
};

export default RegistrationForm;
